import type { PIconProps } from "@porsche-design-system/components-react"
import type { PublishingRuleViolationsView } from "@slfinrtl/ui-bff-routes"
import { PublishingRuleViolationVisibility } from "@slfinrtl/ui-bff-routes"
import { defineMessages, useIntl } from "react-intl"
import type { TagIconProps } from "../tagIcon/TagIcon"
import { TagIcon } from "../tagIcon/TagIcon"
import { Tooltip } from "../tooltip/Tooltip"
import { ViolatedPublishingRulesTooltip } from "./violatedPublishingRulesTooltip/ViolatedPublishingRulesTooltip"

const messages = defineMessages({
	incompleteTooltip: {
		defaultMessage: "Required information for creation is missing",
		id: "listingVisibility.incomplete.tooltip",
	},
	publicTooltip: {
		defaultMessage: "The listing can be found in the public vehicle search",
		id: "listingVisibility.public.tooltip",
	},
	unlistedTooltip: {
		defaultMessage: "The listing is available to anyone who has the link",
		id: "listingVisibility.unlisted.tooltip",
	},
})

const iconMap: Record<PublishingRuleViolationVisibility, PIconProps["name"]> = {
	[PublishingRuleViolationVisibility.INVALID]: "error",
	[PublishingRuleViolationVisibility.INVALID_PREVIEW]: "warning",
	[PublishingRuleViolationVisibility.PREVIEW]: "view-off",
	[PublishingRuleViolationVisibility.VISIBLE_IN_SEARCH]: "view",
}

const colorMap: Record<
	Exclude<PublishingRuleViolationVisibility, "VISIBLE_IN_SEARCH" | "PREVIEW">,
	TagIconProps["color"]
> = {
	[PublishingRuleViolationVisibility.INVALID]: "error",
	[PublishingRuleViolationVisibility.INVALID_PREVIEW]: "warning",
}

interface VisibilityIconProps {
	className?: string
	view: PublishingRuleViolationsView
}
export const VisibilityIcon = ({ view, ...rest }: VisibilityIconProps) => {
	const { formatMessage } = useIntl()

	const color = colorMap[view.visibility as keyof typeof colorMap]
	const iconName = iconMap[view.visibility]

	const iconOrTag = <TagIcon color={color} iconName={iconName} {...rest} />

	return view.errors.length || view.warnings.length ? (
		<ViolatedPublishingRulesTooltip
			title={formatMessage(messages.incompleteTooltip)}
			violatedPublishingRules={[...view.errors, ...view.warnings]}
		>
			{iconOrTag}
		</ViolatedPublishingRulesTooltip>
	) : (
		iconOrTag && (
			<Tooltip
				appendToBody
				content={formatMessage(
					view.visibility === PublishingRuleViolationVisibility.PREVIEW
						? messages.unlistedTooltip
						: messages.publicTooltip,
				)}
			>
				{iconOrTag}
			</Tooltip>
		)
	)
}
